import styled from 'styled-components';
import { compose, fontSize, variant } from 'styled-system';

import {
  BORDER,
  COMMON,
  get,
  LAYOUT,
  SystemCommonProps,
  SystemLayoutProps
} from '../theme/constants';
import sx, { SxProp } from '../theme/utils/sx';
import { Status } from './types';

const composeSystem = compose(COMMON, LAYOUT, BORDER, fontSize);

/** Timeline styles */
export interface StyledTimelineProps
  extends SxProp,
    SystemCommonProps,
    SystemLayoutProps {}

export const StyledTimeline = styled.ol`
  overflow: hidden;
  margin: 0;
  padding: 0;
  ${composeSystem}
  ${sx}
`;

/** TimelineItem styles */

const lineVariant = variant({
  prop: 'status',
  variants: {
    PENDING: {
      background: get('colors.grey.10')
    },
    ACTIVE: {
      background: get('colors.green.30')
    },
    COMPLETED: {
      background: get('colors.green.30')
    }
  }
});

export const StyledLine = styled.div<{ status: Status; order: number }>`
  width: 1px;
  bottom: 0px;
  height: 99999px;
  position: absolute;
  z-index: ${({ order }) => 999 - order * 100 - 1};
  left: 20px;
  ${lineVariant}
`;

export interface StyledTimelineItemProps
  extends SxProp,
    SystemCommonProps,
    SystemLayoutProps {
  subItem?: boolean;
}

export const StyledItemContainer = styled.div<{ subItem: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-top: 24px;
  position: relative;
  margin-top: ${({ subItem }) => (subItem ? '-16px' : '0px')};
`;

export const StyledListItem = styled.li<StyledTimelineItemProps>`
  margin: 0;
  padding: 0;
  list-style: none;
  ${composeSystem}
  ${sx}

  &:first-child ${StyledLine} {
    ${({ subItem }) => !subItem && 'display: none'};
  }

  &:last-child ${StyledLine} {
    ${({ subItem }) => !subItem && 'max-height: 24px; top: 0'};
  }

  &:first-child ${StyledItemContainer} {
    padding-top: 0px;
    margin-top: ${({ subItem }) => (subItem ? '8px' : '0px')};
  }
`;

const iconVariant = variant({
  prop: 'status',
  variants: {
    PENDING: {
      background: get('colors.grey.10'),
      color: get('colors.grey.60'),
      borderColor: get('colors.grey.10')
    },
    ACTIVE: {
      background: 'white',
      color: get('colors.green.30'),
      borderColor: get('colors.green.30')
    },
    COMPLETED: {
      background: get('colors.green.30'),
      color: 'white',
      borderColor: get('colors.green.30')
    }
  }
});

const iconSubitem = ({ subItem, theme }) =>
  subItem && `background: ${theme.colors.green[30]}; border: none;`;

export const StyledIconContainer = styled.div<{
  status: Status;
  order: number;
  subItem: boolean;
}>`
  box-sizing: border-box;
  height: ${({ subItem }) => (subItem ? '12px' : '40px')};
  flex-basis: ${({ subItem }) => (subItem ? '12px' : '40px')};
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  margin: ${({ subItem }) => (subItem ? '14px' : '0px')};
  margin-right: ${({ subItem }) => (subItem ? '32px' : '18px')};
  z-index: 1000;
  ${iconVariant}
  ${iconSubitem}
`;

export const StyledTitle = styled.h4<{ status: Status; subItem: boolean }>`
  margin: 0;
  font-style: normal;
  font-weight: ${({ subItem }) => (subItem ? 'normal' : '600')};
  font-size: 14px;
  line-height: 21px;
  margin-top: 10px;
  color: ${({ theme, status }) =>
    status === 'PENDING' ? theme.colors.grey[60] : theme.colors.grey[90]};
`;

export const StyledDescription = styled.p<{ status: Status }>`
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  margin: 0;
  color: ${({ theme, status }) =>
    status === 'PENDING' ? theme.colors.grey[60] : theme.colors.grey[80]};
`;

export const StyledDate = styled(StyledDescription)`
  color: ${({ theme, status }) =>
    status === 'PENDING' ? theme.colors.grey[60] : theme.colors.grey[70]};
`;
